import type { StudentSummary } from '@/services/api/student-summary';
import { fetchApi, fetchApiBlob } from '@/services/api-client';

export type ConferenceEventStatus = 'draft' | 'open' | 'cancelled';

export interface ConferenceSessionItem {
  time?: string;
  title: string;
  description?: string;
}

/** A block as the API returns it: `kind` is always present. */
export type ConferenceBlock =
  | { kind: 'appointments'; start: string; end: string }
  | { kind: 'session'; start: string; end: string; title: string; items: ConferenceSessionItem[] };

/** A block as sent when writing a day's schedule: `kind` may be omitted (defaults to
 *  `appointments`). The form always sends it explicitly. */
export interface ConferenceBlockInput {
  kind?: 'appointments' | 'session';
  start: string;
  end: string;
  title?: string;
  items?: ConferenceSessionItem[];
}

export interface ConferenceDay {
  date: string;
  blocks: ConferenceBlock[];
}

export interface ConferenceSummary {
  id: string;
  title: string;
  status: ConferenceEventStatus;
  closed: boolean;
  gradeLevels: number[];
  firstDate: string;
  /** Null when the event has no appointment blocks: nothing to book, never "closed". */
  bookingClosesAt: string | null;
  /** A draft's scheduled opening; null once open, or when the office will open it by hand. */
  bookingOpensAt: string | null;
  bookedSlots: number;
  totalSlots: number;
  familiesBooked: number;
  familiesInvited: number;
}

export interface ConferenceTeacherRow {
  id: string;
  teacherId: string;
  teacherName: string;
  teacherNickname: string | null;
  teacherPhotoUrl: string;
  room: string | null;
  subjectLabel: string;
  gradeLevels: number[];
  breaks: string[];
  bookings: number;
}

export interface ConferenceDetail extends ConferenceSummary {
  slotMinutes: number;
  days: ConferenceDay[];
  teachers: ConferenceTeacherRow[];
  invitedAt: string | null;
  lastReminderAt: string | null;
  familiesWithoutBookings: number;
}

export interface ScheduleBooking {
  id: string;
  conferenceTeacherId: string;
  startsAt: string;
  students: StudentSummary[];
  bookedByName: string | null;
  bookedByStaff: boolean;
  createdAt: string;
}

export type ScheduleCell =
  | { startsAt: string; state: 'free' }
  | { startsAt: string; state: 'break' }
  | { startsAt: string; state: 'booked'; booking: ScheduleBooking };

export interface ConferenceSchedule {
  event: ConferenceDetail;
  slots: { startsAt: string; date: string; time: string; blockIndex: number }[];
  columns: { teacher: ConferenceTeacherRow; cells: ScheduleCell[] }[];
}

export interface MyReportComments {
  studentId: string;
  periodName: string;
  comments: { label: string; comment: string }[];
}

export type MyConferenceSlot =
  | {
      startsAt: string;
      date: string;
      time: string;
      state: 'free' | 'break';
      students: [];
    }
  | {
      startsAt: string;
      date: string;
      time: string;
      state: 'booked';
      bookingId: string;
      students: StudentSummary[];
      bookedByName: string | null;
      bookedByStaff: boolean;
      /** Every active parent of the booked students. */
      parentNames: string[];
      /** The teacher's private note on this talk. */
      note: string | null;
      /** The teacher's own comments on each student's most recent report card that has one. */
      reportComments: MyReportComments[];
    }
  | {
      startsAt: string;
      date: string;
      time: string;
      state: 'session';
      students: [];
      /** HH:mm school time; `time` is the block's start. */
      end: string;
      title: string;
      items: ConferenceSessionItem[];
    };

export interface MyConference {
  eventId: string;
  title: string;
  slotMinutes: number;
  room: string | null;
  subjectLabel: string;
  days: ConferenceDay[];
  /** Appointment slots and session rows together, in time order. */
  slots: MyConferenceSlot[];
}

export interface ConferenceDayInput {
  date: string;
  blocks: ConferenceBlockInput[];
}

export interface ConferenceInput {
  title: string;
  gradeLevels: number[];
  slotMinutes: 10 | 15 | 20 | 30;
  /** Null for a session-only event: nothing to book, so no deadline. */
  bookingClosesAt: string | null;
  /** Null: the office opens booking by hand. */
  bookingOpensAt: string | null;
  days: ConferenceDayInput[];
}

const json = (body: unknown) => ({ body: JSON.stringify(body) });

export const conferencesApi = {
  list: () => fetchApi<ConferenceSummary[]>('/conferences'),
  get: (id: string) => fetchApi<ConferenceDetail>(`/conferences/${id}`),
  create: (input: ConferenceInput) => fetchApi<ConferenceDetail>('/conferences', { method: 'POST', ...json(input) }),
  update: (id: string, input: Partial<ConferenceInput>) =>
    fetchApi<ConferenceDetail>(`/conferences/${id}`, { method: 'PATCH', ...json(input) }),
  resync: (id: string) => fetchApi<ConferenceDetail>(`/conferences/${id}/resync`, { method: 'POST' }),
  addTeacher: (id: string, teacherId: string) =>
    fetchApi<ConferenceDetail>(`/conferences/${id}/teachers`, { method: 'POST', ...json({ teacherId }) }),
  updateTeacher: (id: string, ctId: string, input: { room?: string; subjectLabel?: string }) =>
    fetchApi<ConferenceDetail>(`/conferences/${id}/teachers/${ctId}`, { method: 'PATCH', ...json(input) }),
  removeTeacher: (id: string, ctId: string) =>
    fetchApi<void>(`/conferences/${id}/teachers/${ctId}`, { method: 'DELETE' }),
  setBreaks: (id: string, ctId: string, startsAt: string[]) =>
    fetchApi<ConferenceDetail>(`/conferences/${id}/teachers/${ctId}/breaks`, { method: 'PUT', ...json({ startsAt }) }),
  schedule: (id: string) => fetchApi<ConferenceSchedule>(`/conferences/${id}/schedule`),
  book: (id: string, input: { conferenceTeacherId: string; startsAt: string; studentIds: string[] }) =>
    fetchApi<ScheduleBooking>(`/conferences/${id}/bookings`, { method: 'POST', ...json(input) }),
  move: (id: string, bookingId: string, startsAt: string) =>
    fetchApi<ScheduleBooking>(`/conferences/${id}/bookings/${bookingId}`, { method: 'PATCH', ...json({ startsAt }) }),
  cancelBooking: (id: string, bookingId: string) =>
    fetchApi<void>(`/conferences/${id}/bookings/${bookingId}`, { method: 'DELETE' }),
  candidates: (id: string, studentId: string) =>
    fetchApi<{ students: StudentSummary[]; conferenceTeacherIds: string[] }>(
      `/conferences/${id}/candidates?studentId=${encodeURIComponent(studentId)}`
    ),
  invite: (id: string) => fetchApi<{ recipients: number }>(`/conferences/${id}/invite`, { method: 'POST' }),
  remind: (id: string) => fetchApi<{ recipients: number }>(`/conferences/${id}/remind`, { method: 'POST' }),
  cancelEvent: (id: string) => fetchApi<void>(`/conferences/${id}/cancel`, { method: 'POST' }),
  mine: () => fetchApi<MyConference[]>('/conferences/mine'),
  saveMyNote: (bookingId: string, note: string | null) =>
    fetchApi<{ note: string | null }>(`/conferences/mine/bookings/${bookingId}/note`, {
      method: 'PUT',
      ...json({ note }),
    }),
  mineIcs: (eventId: string) => fetchApiBlob(`/conferences/mine/${eventId}/ics`),
  calendarLink: () => fetchApi<{ token: string | null }>('/conferences/mine/calendar-link'),
  ensureCalendarLink: () => fetchApi<{ token: string }>('/conferences/mine/calendar-link', { method: 'POST' }),
  resetCalendarLink: () => fetchApi<{ token: string }>('/conferences/mine/calendar-link/reset', { method: 'POST' }),
};
